import type { StatId, StatMod } from '../content/types';

export type Stats = Record<StatId, number>;

/** Baseline player stats before meta upgrades, equipment, and run upgrades. */
export const BASE_STATS: Stats = {
  damage: 10,
  damageMult: 1,
  fireRate: 1,
  projectiles: 0,
  pierce: 0,
  bounce: 0,
  critChance: 0.05,
  critMult: 1.5,
  projectileSpeed: 1,
  projectileSize: 1,
  range: 16,
  maxHp: 120,
  armor: 0,
  regen: 0,
  lifesteal: 0,
  moveSpeed: 6,
  dashCooldown: 1.6,
  dashDistance: 4,
  dashCharges: 1,
  dashIframes: 0.25,
  pickupRadius: 3.5,
  xpMult: 1,
  shardMult: 1,
  luck: 0,
  movingFireMult: 0,
  dashFireMult: 0,
  stillBonusWindow: 0,
  stillBonusMult: 1,
  stillRamp: 0,
  stillRampMax: 0,
  doubleTap: 0,
  doubleTapDelay: 0.12,
  doubleTapDamage: 0.6,
  fireChance: 0,
  fireDps: 0,
  frostChance: 0,
  frostSlow: 0,
  lightningChance: 0,
  lightningChains: 0,
  temporalChance: 0,
  temporalSlow: 0,
  temporalRewind: 0,
  temporalPulseInterval: 8,
};

export const STAT_IDS = Object.keys(BASE_STATS) as StatId[];

export function cloneStats(s: Stats): Stats {
  return { ...s };
}

/**
 * Deterministic stat resolution: all `add`s are summed first, then all `mul`s
 * are multiplied. Order of mods never matters, which keeps snapshots and
 * multiplayer resolution simple. Sources are layered meta → character passive → equipment → run.
 */
export function applyMods(base: Stats, modLists: readonly (readonly StatMod[])[], out: Stats = cloneStats(base)): Stats {
  for (const id of STAT_IDS) out[id] = base[id];
  for (const mods of modLists) for (const m of mods) if (m.add) out[m.stat] += m.add;
  for (const mods of modLists) for (const m of mods) if (m.mul !== undefined) out[m.stat] *= m.mul;
  // Clamps
  out.critChance = Math.min(out.critChance, 1);
  out.fireChance = Math.min(out.fireChance, 1);
  out.frostChance = Math.min(out.frostChance, 1);
  out.lightningChance = Math.min(out.lightningChance, 1);
  out.temporalChance = Math.min(out.temporalChance, 1);
  out.frostSlow = Math.min(out.frostSlow, 0.8);
  out.temporalSlow = Math.min(out.temporalSlow, 0.9);
  out.maxHp = Math.max(1, out.maxHp);
  out.dashCooldown = Math.max(0.3, out.dashCooldown);
  out.fireRate = Math.max(0.1, out.fireRate);
  out.projectiles = Math.max(0, Math.round(out.projectiles));
  out.pierce = Math.max(0, Math.round(out.pierce));
  out.bounce = Math.max(0, Math.round(out.bounce));
  out.dashCharges = Math.max(1, Math.round(out.dashCharges));
  return out;
}
